import CircularProgress from '@mui/material/CircularProgress'

import './style.scss'

import { notificationUtils } from 'features/notifications/index.ts'
import { type Orders, ordersApi } from 'features/orders'
import { api as subscriptionApi } from 'features/subscription/api.ts'
import { type Plan, usePlans } from 'features/subscription/hooks/usePlans.ts'
import { useState } from 'react'
import { Navigate } from 'react-router-dom'

import board from '../../assets/image/board.png'
import doneSvg from '../../assets/image/icons/svg/done.svg'
import ModalWindow from '../../components/molecules/ModalWindow'
import { CONSTS } from '../../config/objectConst'
import { appSelectors } from '../../redux/reducers/app.ts'
import { useAppSelector } from '../../shared/hooks'
import { useCheckout } from './hooks/useCheckout.ts'
import { useLeftMenu } from './hooks/useLeftMenu.ts'

declare global {
	interface Window {
		fastSpringCheckoutClosed: any
	}
}

const INITIAL_MODAL: {
	id: string
	mode: 'downgrade' | 'upgrade'
} = {
	id: '',
	mode: 'downgrade'
}

export function UpgradePage() {
	const isUpgraded = useAppSelector(appSelectors.isUpgraded)
	return isUpgraded ? <Navigate to='/campaigns' /> : <InnerUpgradePage />
}

function InnerUpgradePage() {
	const {
		data: subscription,
		isSuccess,
		...queryOptions
	} = subscriptionApi.useSubscriptionQuery()
	const [upgrade, mutationOptions] = subscriptionApi.useUpgradeMutation()
	const [syncOrders, ordersOptions] = ordersApi.useSyncMutation()
	const refreshAccessToken = useAppSelector(appSelectors.refreshAccessToken)

	const [modal, setModal] = useState(INITIAL_MODAL)

	useLeftMenu()
	const plans = usePlans()

	const openCheckout = useCheckout()

	window.fastSpringCheckoutClosed = fastSpringCheckoutClosed

	const changeSubscription = ({ id, price }: Plan) => {
		if (!isSuccess) {
			return
		}

		const shouldUpgrade = subscription.base_price < price

		setModal({
			id,
			mode: shouldUpgrade ? 'upgrade' : 'downgrade'
		})
	}

	async function fastSpringCheckoutClosed(data: Orders.Body) {
		if (!data) {
			return
		}

		await syncOrders(data).unwrap()
		await refreshAccessToken()
		notificationUtils.snackById('3.19')
	}

	const upgradeSubscription = async (planId: string) => {
		setModal(INITIAL_MODAL)
		await upgrade(planId).unwrap()
		await refreshAccessToken()
		notificationUtils.snackById('n3.22')
	}

	const isLoading =
		queryOptions.isLoading ||
		mutationOptions.isLoading ||
		ordersOptions.isLoading

	const isTrial =
		isSuccess && subscription.plan_id === CONSTS.SUBSCRIPTIONS.TRIAL.ID
	const isActive = isSuccess && subscription.is_active

	const modalText =
		CONSTS.confirmationDialogs[modal.mode === 'upgrade' ? '1.11' : '1.12']
	return (
		<main className='main app-scroll' style={{ position: 'relative' }}>
			{isLoading ? (
				<div className='material-loader-wrapper'>
					<CircularProgress
						classes={{ colorPrimary: 'material-loader' }}
						color='primary'
						size={60}
					/>
				</div>
			) : (
				<div className='containerUpgrade'>
					<h2 className='containerUpgrade__main-title'>
						Choose subscription plan
					</h2>

					<div className='containerUpgrade__wrapper'>
						<img
							alt='board'
							className='containerUpgrade__team-img'
							src={board}
						/>
						{plans.map((plan, i) => (
							<div
								className={
									plan.active
										? 'containerUpgrade__individual-active'
										: 'containerUpgrade__individual'
								}
								key={i}
							>
								<h2 className='containerUpgrade__title-accent'>{plan.name}</h2>
								<p className='containerUpgrade__description'>
									{plan.description}
								</p>
								<div className='containerUpgrade__line' />
								<p className='containerUpgrade__price'>
									{plan.price}$ per user/month
								</p>
								{
									<>
										{plan.active ? (
											<button
												className={
													'containerUpgrade__button-subscription-active'
												}
											>
												Current plan
											</button>
										) : (
											<button
												className={'containerUpgrade__button-subscription'}
												onClick={() =>
													isTrial || !isActive
														? openCheckout(plan.path)
														: changeSubscription(plan)
												}
											>
												Upgrade subscription
											</button>
										)}
									</>
								}
								{renderPlanFeatures(plan)}
							</div>
						))}
					</div>
					<div className='containerUpgrade__container-link'>
						<a
							className='containerUpgrade__link'
							href='https://snaily.io/terms-and-conditions/'
							rel='noopener noreferrer'
							target='_blank'
						>
							Terms and Conditions
						</a>
					</div>
				</div>
			)}

			<ModalWindow
				closeHandler={() => setModal(INITIAL_MODAL)}
				closeText='No'
				confirmHandler={() => upgradeSubscription(modal.id)}
				confirmText='Yes'
				open={Boolean(modal.id)}
				{...modalText}
			/>
		</main>
	)
}

function renderPlanFeatures(plan: Plan) {
	const includedPrefix = plan.arrayData[0]?.startsWith('ALL ')
		? plan.arrayData[0]
		: null
	const items = includedPrefix ? plan.arrayData.slice(1) : plan.arrayData
	const heading = includedPrefix ? (
		<p
			className='containerUpgrade__text'
			style={{ fontWeight: 700, marginBottom: 12 }}
		>
			{includedPrefix}
		</p>
	) : (
		<p className='containerUpgrade__text'>Features overview:</p>
	)

	if (plan.featureColumns) {
		const result = (
			<>
				{heading}
				<div className='containerUpgrade__features'>
					{plan.featureColumns.map(column => (
						<ul className='containerUpgrade__list' key={column[0]}>
							{renderFeatureItems(column)}
						</ul>
					))}
				</div>
			</>
		)
		return result
	}

	const result = (
		<>
			{heading}
			<ul className='containerUpgrade__list'>
				{renderFeatureItems(items)}
			</ul>
		</>
	)
	return result
}

function renderFeatureItems(items: string[]) {
	const result = items.map(item => (
		<li className='containerUpgrade__item' key={item}>
			<img
				alt='done'
				className='containerUpgrade__img'
				src={doneSvg}
			/>
			{item}
		</li>
	))
	return result
}
